import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { ChartOptions, ChartData, ChartDataset, Chart, TooltipItem } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

@Component({
  selector: 'app-area-chart',
  templateUrl: './area-chart.component.html',
  styleUrls: ['./area-chart.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AreaChartComponent implements OnChanges, AfterViewInit {
  @Input() title: string = '';
  @Input() labels: string[] = [];
  @Input() currentData: number[] = [];
  @Input() previousData: number[] = [];
  @Input() currentLabel: string = 'Current period';
  @Input() previousLabel: string = 'Previous period';
  @Input() summaryLabel: string = '';
  @Input() periodOptions: { label: string; value: any }[] = [];
  @Output() periodChange = new EventEmitter<any>();

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  public chartData: ChartData<'line'> = { labels: [], datasets: [] };
  public chartOptions: ChartOptions<'line'> = {};
  private isAllZeroInternal = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['labels'] || changes['currentData'] || changes['previousData'] ||
        changes['currentLabel'] || changes['previousLabel'])
      && this.labels && this.currentData && this.previousData
    ) {
      this.buildChart();
      if (this.chart && this.chart.chart) {
        setTimeout(() => this.applyGradientToDataset(), 0);
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.chart && this.chart.chart) {
      this.applyGradientToDataset();
    }
  }

  private buildChart() {
    this.isAllZeroInternal = (!this.currentData || this.currentData.length === 0 || this.currentData.every(v => !v)) && 
                      (!this.previousData || this.previousData.length === 0 || this.previousData.every(v => !v));
    const isAllZero = this.isAllZeroInternal;

    let finalCurrentData = [...(this.currentData || [])];
    let finalPreviousData = [...(this.previousData || [])];
    let currentLineColor = 'rgba(54, 162, 235, 1)';
    const previousLineColor = 'rgba(100, 100, 100, 0.6)';
    let placeholderBg = 'rgba(54, 162, 235, 0.4)';

    if (isAllZero) {
      currentLineColor = '#e9ecef';
      placeholderBg = 'rgba(233, 236, 239, 0.4)';
      
      if (!this.labels || this.labels.length === 0) {
        this.labels = ['1', '2', '3', '4', '5', '6', '7'];
        finalCurrentData = [0.1, 0.5, 1.2, 2, 1.2, 0.5, 0.1]; // Bell curve placeholder
        finalPreviousData = [0, 0, 0, 0, 0, 0, 0];
      } else {
        finalCurrentData = this.labels.map(() => 1);
        finalPreviousData = this.labels.map(() => 0);
      }
    }

    const currentDataset: ChartDataset<'line'> = {
      label: this.currentLabel,
      data: finalCurrentData,
      borderColor: currentLineColor,
      backgroundColor: placeholderBg,
      fill: 'start',
      tension: 0.5,
      pointRadius: 0,
      borderWidth: 2
    };
    const previousDataset: ChartDataset<'line'> = {
      label: this.previousLabel,
      data: finalPreviousData,
      borderColor: previousLineColor,
      borderDash: [5, 5],
      fill: false,
      tension: 0.5,
      pointRadius: 0,
      borderWidth: 1.5
    };

    this.chartData = {
      labels: this.labels,
      datasets: [currentDataset, previousDataset]
    };

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: { display: false },
          ticks: { autoSkip: true, maxRotation: 0 }
        },
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(200,200,200,0.3)' },
          ticks: {
            stepSize: isAllZero ? 1 : undefined,
            callback: (value) => {
              if (isAllZero) {
                return value === 1 ? '1' : '0';
              }
              if (typeof value === 'number') {
                if (value >= 1000) {
                  return `${(value / 1000).toFixed(0)}k`;
                }
                return `${value}`;
              }
              return value;
            }
          }
        }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => {
              if (isAllZero) return '0';
              const labelPrefix = context.dataset.label ? `${context.dataset.label}: ` : '';
              const y = context.parsed.y;
              if (typeof y === 'number') {
                if (y >= 1000) {
                  return `${labelPrefix}$${(y / 1000).toFixed(0)}k`;
                }
                return `${labelPrefix}$${y}`;
              }
              return `${labelPrefix}${y}`;
            }
          }
        }
      }
    };
  }

  private applyGradientToDataset() {
    if (!this.chart) return;
    const chartRef = this.chart.chart;
    if (!chartRef) return;
    const canvas = chartRef.canvas as HTMLCanvasElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    if (this.isAllZeroInternal) {
      gradient.addColorStop(0, 'rgba(233, 236, 239, 0.4)');
      gradient.addColorStop(1, 'rgba(233, 236, 239, 0)');
    } else {
      gradient.addColorStop(0, 'rgba(54, 162, 235, 0.4)');
      gradient.addColorStop(1, 'rgba(54, 162, 235, 0)');
    }

    if (this.chartData.datasets && this.chartData.datasets.length > 0) {
      this.chartData.datasets[0].backgroundColor = gradient as any;
      this.chart.update();
    }
  }

  onPeriodChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    this.periodChange.emit(select.value);
  }
}
